import React from 'react';
import { FmBanner } from './FmBanner';
import { FmTable, type FmTableColumn } from './FmTable';
import { FmTextField } from './FmTextField';
import { ProductAction } from './ProductAction';
import type {
  FmProductHeroDescriptor,
  FmProductHeroForecast,
  FmProductHeroForecastReady,
  FmProductHeroSlide,
} from './FmProductPage.types';

export const HeroState: React.FC<{ state: FmProductHeroDescriptor['state'] }> = ({ state }) => {
  if (state.status === 'ready') return null;
  const action =
    state.status === 'locked' ? state.action : state.status === 'error' ? state.retry : undefined;
  // The hero paints imagery behind its copy; a tinted banner straight on it lets
  // the image bleed through and leaves dark text on dark, so it sits on an
  // opaque light surface.
  return (
    <div className="fm-product-hero__state">
      <FmBanner
        status={state.status}
        title={state.label}
        description={'detail' in state && state.detail ? state.detail : undefined}
        action={action ? <ProductAction action={action} /> : undefined}
        data-testid={`product-hero-state-${state.status}`}
      />
    </div>
  );
};

export const LeadInteraction: React.FC<{
  interaction: NonNullable<FmProductHeroDescriptor['leadInteraction']>;
}> = ({ interaction }) => (
  <form
    className="fm-product-hero__lead"
    onSubmit={(event) => {
      event.preventDefault();
      interaction.onSubmit(interaction.value);
    }}
    data-purpose={interaction.purpose}
  >
    <label className="fm-product-hero__lead-label" htmlFor={`product-lead-${interaction.purpose}`}>
      {interaction.label}
    </label>
    <div className="fm-product-hero__lead-controls">
      <FmTextField
        id={`product-lead-${interaction.purpose}`}
        type="search"
        prefixIconKey="search"
        value={interaction.value}
        placeholder={interaction.placeholder}
        ariaLabel={interaction.label}
        onChange={(event) => interaction.onValueChange(event.target.value)}
        data-testid="product-hero-lead-input"
        trailingSubmit={{
          ariaLabel: interaction.submitLabel,
          testId: 'product-hero-lead-submit',
        }}
      />
    </div>
    {interaction.helperText ? (
      <span className="fm-product-hero__lead-helper">{interaction.helperText}</span>
    ) : null}
  </form>
);

interface HeroAlternativeRow {
  id: string;
  rank: number | string;
  objective: string;
  npv: string;
  net: string;
}

function formatEur(value: number, locale: string, currency: boolean): string {
  return new Intl.NumberFormat(locale, {
    ...(currency ? { style: 'currency' as const, currency: 'EUR' } : {}),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function heroAlternativeRows(forecast: FmProductHeroForecastReady): HeroAlternativeRow[] {
  const labels = {
    'whole-period-value': forecast.copy.choicesValueLabel,
    'near-term-revenue': forecast.copy.choicesNearTermLabel,
    'avoid-clearcuts': forecast.copy.choicesAvoidClearfellLabel,
    tapio: forecast.copy.choicesTapioLabel,
  } as const;
  return forecast.alternatives.map((alternative, index) => ({
    id: alternative.id,
    rank: alternative.status === 'ready' ? index + 1 : '—',
    objective: labels[alternative.id],
    npv:
      alternative.npvPerHectareEur === null
        ? forecast.copy.unavailableValueLabel
        : formatEur(alternative.npvPerHectareEur, forecast.displayLocale, false),
    net:
      alternative.netCashEur === null
        ? forecast.copy.unavailableValueLabel
        : formatEur(alternative.netCashEur, forecast.displayLocale, true),
  }));
}

function heroAlternativeColumns(
  forecast: FmProductHeroForecast,
): FmTableColumn<HeroAlternativeRow>[] {
  return [
    { key: 'rank', header: '#', label: '#', numeric: true, role: 'numeric' },
    {
      key: 'objective',
      header: forecast.copy.choicesPlanLabel,
      label: forecast.copy.choicesPlanLabel,
    },
    {
      key: 'npv',
      header: forecast.copy.choicesNpvLabel,
      label: forecast.copy.choicesNpvLabel,
      numeric: true,
      role: 'money',
    },
    {
      key: 'net',
      header: forecast.copy.choicesNetLabel,
      label: forecast.copy.choicesNetLabel,
      numeric: true,
      role: 'money',
    },
  ];
}

export const HeroChoices: React.FC<{
  forecast: FmProductHeroForecastReady;
  property: string;
}> = ({ forecast, property }) => {
  const alternatives = heroAlternativeRows(forecast);
  const hasReadyAlternatives = alternatives.some((alternative) => alternative.rank !== '—');
  return (
    <section
      className="fm-product-hero__forecast"
      aria-label={property}
      data-testid="product-hero-forecast-choices"
    >
      <p className="fm-product-hero__forecast-property">
        {property}
        {hasReadyAlternatives ? ` · ${forecast.scopeLabel}` : ''}
      </p>
      <div className="fm-product-hero__choices-table">
        <FmTable<HeroAlternativeRow>
          columns={heroAlternativeColumns(forecast)}
          rows={alternatives}
          rowKey={(row) => row.id}
          caption={forecast.copy.choicesTableCaption}
          density="compact"
          nonInteractiveHead
          data-testid="product-hero-alternatives-table"
        />
      </div>
    </section>
  );
};

export const HeroSlideCopy: React.FC<{
  descriptor: FmProductHeroDescriptor;
  slide: FmProductHeroSlide;
  headingId?: string;
}> = ({ descriptor, slide, headingId }) => {
  const copy = descriptor.slideCopy?.[slide];
  return (
    <div className="fm-product-hero__copy">
      {descriptor.eyebrow ? (
        <span className="fm-product-hero__eyebrow">{descriptor.eyebrow}</span>
      ) : null}
      <h1 id={headingId} className="fm-product-hero__title">
        {copy?.title ?? descriptor.title}
      </h1>
      <p className="fm-product-hero__summary">{copy?.summary ?? descriptor.summary}</p>
      <ul className="fm-product-hero__proof">
        {(copy?.proofPoints ?? descriptor.proofPoints).map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  );
};
